import React, { useState, useRef, useEffect } from 'react';
import { PlusIcon, SearchIcon, SortIcon } from './icons';
import { SortOption, SORT_LABELS } from './types';
import styles from '../LeftSideBar.module.scss';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';

interface TreeSectionProps {
  title: string;
  count: number;
  onAdd?: () => void | Promise<void>;
  query: string;
  onQuery: (v: string) => void;
  sort: SortOption;
  onSort: (v: SortOption) => void;
  children: React.ReactNode;
  emptyText: string;
  empty: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  disabled?: boolean;
  modalOpen?: boolean;
}

const TreeSection: React.FC<TreeSectionProps> = ({
  title,
  count,
  onAdd,
  query,
  onQuery,
  sort,
  onSort,
  children,
  emptyText,
  empty,
  collapsed,
  onToggleCollapsed,
  disabled = false,
  modalOpen = false,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
    if (!searchOpen) onQuery('');
  }, [searchOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sortOpen) return;
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortOpen]);

  useEffect(() => {
    if (count === 0) {
      setSearchOpen(false);
      setSortOpen(false);
      onQuery('');
    }
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`${styles.segment} ${collapsed ? styles.segmentCollapsed : styles.segmentExpanded}`}>
      <div
        className={`${styles.sectionHeader} ${collapsed ? styles.sectionHeaderCollapsed : ''}`}
        role="button"
        tabIndex={0}
        onClick={onToggleCollapsed}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleCollapsed();
          }
        }}
        aria-expanded={!collapsed}
      >
        <span className={styles.sectionTitle}>{title}</span>
        <span className={styles.sectionCount}>({count})</span>
        <span style={{ flex: 1 }} />

        <div className={styles.headerActions}>
          <CustomToolTip title="Search">
            <button
              className={`${styles.lpAdd} ${searchOpen ? styles.lpAddActive : ''}`}
              type="button"
              disabled={count === 0}
              onClick={(e) => {
                e.stopPropagation();
                setSearchOpen((o) => !o);
                setSortOpen(false);
              }}
            >
              <SearchIcon />
            </button>
          </CustomToolTip>

          <div className={styles.sortWrap} ref={sortRef} onClick={(e) => e.stopPropagation()}>
            <CustomToolTip title="Sort">
              <button
                className={`${styles.lpAdd} ${sortOpen ? styles.lpAddActive : ''}`}
                type="button"
                disabled={count === 0}
                onClick={() => {
                  setSortOpen((o) => !o);
                  setSearchOpen(false);
                }}
              >
                <SortIcon />
              </button>
            </CustomToolTip>
            {sortOpen && (
              <div className={styles.sortDropdown}>
                {(Object.keys(SORT_LABELS) as SortOption[]).map((opt) => (
                  <button
                    key={opt}
                    className={`${styles.sortOption} ${sort === opt ? styles.sortOptionActive : ''}`}
                    type="button"
                    onClick={() => {
                      onSort(opt);
                      setSortOpen(false);
                    }}
                  >
                    {SORT_LABELS[opt]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {onAdd && (
            <CustomToolTip title={title === 'Satellites' ? 'Create Satellite(s)' : `Add ${title.slice(0, -1)}`}>
              <button
                className={`${styles.lpAdd} ${adding || modalOpen ? styles.lpAddActive : ''}`}
                type="button"
                disabled={adding || disabled}
                onClick={async (e) => {
                  e.stopPropagation();
                  const result = onAdd();
                  if (result instanceof Promise) {
                    setAdding(true);
                    try {
                      await result;
                    } finally {
                      setAdding(false);
                    }
                  }
                }}
              >
                {adding ? <span className={styles.btnSpinner} /> : <PlusIcon />}
              </button>
            </CustomToolTip>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          {searchOpen && (
            <div className={styles.searchBar}>
              <span className={styles.searchIcon}>
                <SearchIcon />
              </span>
              <input
                ref={inputRef}
                className={styles.searchInput}
                placeholder={`Search ${title.toLowerCase()}…`}
                value={query}
                onChange={(e) => onQuery(e.target.value)}
              />
              {query && (
                <button className={styles.clearBtn} onClick={() => onQuery('')} type="button">
                  ×
                </button>
              )}
            </div>
          )}

          <div className={styles.segmentList}>
            {empty && !query && onAdd && !disabled && (
              <div className={styles.emptyCta} onClick={onAdd}>
                + Create {title === 'GeoFeatures' ? 'GeoFeatures' : title.toLowerCase().replace('ground segment', 'ground station').replace('geofeatures', 'geo feature').replace('satellite(s)', 'satellite(s)')}
              </div>
            )}
            {empty && query && <div className={styles.empty}>{`No results for "${query}"`}</div>}
            {empty && !query && !onAdd && <div className={styles.empty}>{emptyText}</div>}
            {children}
          </div>
        </>
      )}
    </div>
  );
};

export default TreeSection;
